import React from 'react';

import { VIEW_TYPE_PREFERENCE_LIST } from 'apps/catalogue/actions/catalogue';

import containerTypes from 'apps/analytics/utils/containerTypes';
import FopPageComponent from 'apps/fops/analytics/components/FopPageComponent';
import FopViewType from '../fopViews/FopViewType';
import ProductLabel from 'apps/products/models/ProductLabel';
import PriceContent from '../fopPartials/PriceContent';
import ProductImpression from 'apps/gtm/components/ProductImpression';
import PromotionImpression from 'apps/gtm/components/PromotionImpression';
import RegularFopControls from '../fopPartials/RegularFopControls';

import { createBasketQuantitiesBySku } from 'apps/basket/utils/createBasketQuantitiesBySku';

import { getCitrusAdData } from 'apps/analytics/utils/citrusAd';
import CitrusAdImpression from 'apps/analytics/components/CitrusAdImpression';

function isOutOfStock(product) {
  return (product.labels || []).includes(ProductLabel.OOS);
}

export default class RegularFopElementsFactory {
  fopViewType = FopViewType.REGULAR_FOP;

  getFopTypeClass = () => `fops-regular ${this.viewTypePreference === VIEW_TYPE_PREFERENCE_LIST
    ? 'fops-list'
    : 'fops-shelf'}`;

  getContainerType = () => containerTypes.REGULAR;

  constructor({
    basketItems,
    betaFeatures,
    onQuantitySubmit,
    productWarnings,
    productsBySku,
    viewTypePreference,
    containerType,
  }) {
    this.betaFeatures = betaFeatures;
    this.productsBySku = productsBySku;
    this.basketQuantityBySku = createBasketQuantitiesBySku(basketItems);
    this.onQuantitySubmit = onQuantitySubmit;
    this.viewTypePreference = viewTypePreference;
    this.hasQuickMenu = true;
    this.productWarnings = productWarnings;
    this.containerType = containerType;
  }

  getProductWarning(sku) {
    return this.getProductWarningsForSku(sku).length > 0;
  }

  getProductWarningsForSku = (sku) => {
    const { productWarnings = {} } = this;
    return productWarnings[sku] || [];
  };

  getFopControlsComponent = ({ product, section, analyticsContextData, collectionFopIdx } = {}) => {
    const { productsPageRouteLocationQuery, externalAdData, analyticsDataBasketItems } = analyticsContextData;

    return (
      <RegularFopControls
        analyticsFopContextDataBasketItems={analyticsDataBasketItems}
        analyticsFopContextDataContainerType={this.getContainerType()}
        analyticsFopContextDataLocationQuery={productsPageRouteLocationQuery}
        analyticsFopContextDataExternalAd={externalAdData}
        product={product}
        productWarning={this.getProductWarning(product.sku)}
        location={section}
        collectionFopIdx={collectionFopIdx}
        basketQuantity={this.getBasketQuantity(product.sku)}
        onQuantitySubmit={this.onQuantitySubmit}
      />
    );
  };

  getFopAnalyticsComponents = ({ fopType, product, fop, section, collectionOrderDetails }) => {
    const result = [
      <FopPageComponent
        key="0"
        fopType={fopType}
        product={product}
        fop={fop}
        section={section}
        collectionOrderDetails={collectionOrderDetails}
      />,
      <ProductImpression
        key="1"
        product={product}
        section={section}
        collectionOrderDetails={collectionOrderDetails}
      />,
    ];
    if (product.offer) {
      result.push(
        <PromotionImpression
          key="2"
          section={section}
          product={product}
          collectionOrderDetails={collectionOrderDetails}
        />
      );
    }

    const { fopAttributes } = fop;
    const citrusAdData = getCitrusAdData({ fopAttributes, betaFeatures: this.betaFeatures });
    if (citrusAdData) {
      result.push(<CitrusAdImpression key="3" {...citrusAdData} />);
    }

    return result;
  };

  getFopMiddleContent = ({ labels, price, isValueDeliveredProduct }) => {
    const zeroAfterPound = !this.betaFeatures || !this.betaFeatures.includes('CUT_ZEROS_IN_FOP_ROUND_POUND');
    return (
      <PriceContent
        price={price}
        productLabels={labels}
        zeroAfterPound={zeroAfterPound}
        isValueDeliveredProduct={isValueDeliveredProduct}
      />
    );
  };

  shouldUpdateFopControls = (sku, nextFactory) => {
    if (nextFactory === undefined) {
      return false;
    }

    if (this.viewTypePreference !== nextFactory.viewTypePreference) {
      return true;
    }

    return this.getBasketQuantity(sku) !== nextFactory.getBasketQuantity(sku)
      || this.getProduct(sku) !== nextFactory.getProduct(sku);
  };

  getBasketQuantity(sku) {
    return this.basketQuantityBySku[sku] || 0;
  }

  getProduct(sku) {
    return this.productsBySku[sku];
  }

  calculateClassNames = (sku) => {
    const product = this.getProduct(sku);
    if (!product) return '';

    if (isOutOfStock(product)) {
      return 'fop-out-of-stock';
    }
    return (this.getBasketQuantity(sku) > 0) ? 'in-trolley' : '';
  };
}
